/**
 * Data handlers for processing API responses
 */
import { getNextUserColor, colorToHex } from '../utils/colors.js';
import { getArtist, getWeeklyArtistChart } from '../api/client.js';
import { SIM_COLOR, MIN_MATCH, DJ_COLORS } from '../config.js';

/**
 * Process user data from API
 * @param {Object} json - API response
 * @param {Object} state - Application state
 * @param {Function} queueChartsCallback - Callback to queue charts
 * @returns {Object|null} Processed user or null if error
 */
export function handleUserResponse(json, state, queueChartsCallback) {
  if (json.error || !json.user) {
    console.error(json.error);
    return { error: json.error };
  }

  const user = json.user;

  // Process charts
  user.charts.forEach((c) => {
    c.wac = [];
    c.date = new Date(c.fromd * 1000);
  });

  user.color = getNextUserColor();
  state.addUser(user);

  queueChartsCallback(user);

  return user;
}

/**
 * Process weekly artist chart data
 * @param {Object} json - API response
 * @param {Object} state - Application state
 * @param {Object} graph - Graph instance
 * @param {Function} getArtistDetailsCallback - Callback to load artist details
 * @returns {Object} Chart data with metadata
 */
export function handleWeeklyArtistChart(json, state, graph, getArtistDetailsCallback) {
  const chartsId = Number.parseInt(json.chartsid, 10);
  const user = state.users.find((u) => u.charts.find((c) => c.id === chartsId));

  if (!user) {
    console.log(`User not found for chart ${chartsId}.`);
    return { error: 'User not found' };
  }

  const chart = user.charts.find((c) => c.id === chartsId);
  chart.wac = json.chartEntries;
  chart.playcount = 0;

  json.chartEntries.forEach((c) => {
    let artist = state.findArtist(c.artistid);

    // Skip low-ranked or low-playcount entries for getting details
    if (c.rank > 10 || c.playcount < 3) {
      if (artist) {
        artist.color = user.color;
        artist.playcount += c.playcount;
        const node = graph.getNode(artist.id);
        if (node) {
          node.data.changed = true;
        }
      }
      return;
    }

    chart.playcount += c.playcount;

    if (!artist) {
      artist = {
        id: c.artistid,
        playcount: c.playcount,
        playRank: 0.01,
        sim: false,
        name: undefined,
        color: user.color,
      };
      state.addArtist(artist);
      getArtistDetailsCallback(artist.id);
    } else {
      artist.playcount += c.playcount;
      artist.labelFrameCount = Math.max(60, artist.labelFrameCount || 0);
      artist.color = user.color;
      if (artist.sim) {
        artist.sim = false;
        getArtistDetailsCallback(artist.id);
      }
    }
  });

  // Update max playcount
  state.targetMaxPlaycount = Math.max(...state.artists.map((a) => a.playcount));
  if (state.maxPlaycount < state.targetMaxPlaycount) {
    state.maxPlaycount += 1;
    graph.forEachNode((n) => {
      n.data.changed = true;
    });
  }

  return { chart, user };
}

/**
 * Get artist color based on DJ plays
 * @param {Object} artist - Artist data
 * @param {Array} djs - Array of DJ data
 * @returns {number} Color hex value
 */
export function getArtistColor(artist, djs) {
  let color = artist.color || SIM_COLOR;

  if (artist.djplays && artist.djplays.length > 0) {
    const randomIndex = Math.floor(Math.random() * artist.djplays.length);
    const dj = djs.find((d) => d.id === artist.djplays[randomIndex].djid);

    if (dj) {
      color = dj.color;
    } else {
      console.warn(`DJ with id ${artist.djplays[randomIndex].djid} not found`);
      color = 0xff001080; // Fallback color
    }
  }

  return color;
}

/**
 * Process artist details from API
 * @param {Object} json - API response
 * @param {Object} state - Application state
 * @param {Object} graph - Graph instance
 * @param {number} w - Window width
 * @param {number} h - Window height
 * @returns {Object} Processed artist data
 */
export function handleArtistResponse(json, state, graph, w, h) {
  const _artist = json.artist;
  const artist = state.findArtist(_artist.id);

  if (!artist) {
    console.error(`${_artist.name} should be in artists array but wasn't found.`);
    return { error: 'Artist not found in state' };
  }

  artist.name = _artist.name;
  artist.mbid = _artist.mbid;
  artist.similars = _artist.similars;
  artist.djplays = _artist.djplays;
  artist.label = artist.name;
  artist.labelFrameCount = 120;
  artist.color = getArtistColor(artist, state.djs);

  // Get or create node
  let node = graph.getNode(artist.id);
  if (!node) {
    node = addNodeToGraph(artist, graph, w, h);
  }
  node.data.changed = true;

  // Add similar artists
  addSimilarArtists(artist, node, state, graph, w, h);

  return artist;
}

/**
 * Add a node to the graph
 * @param {Object} artist - Artist data
 * @param {Object} graph - Graph instance
 * @param {number} w - Window width (unused, kept for compatibility)
 * @param {number} h - Window height (unused, kept for compatibility)
 * @param {Object} similarToNode - Optional node to place near
 * @returns {Object} Created node
 */
export function addNodeToGraph(artist, graph, w, h, similarToNode = null) {
  // Position nodes in world space around origin (0, 0)
  // Camera will handle centering in viewport
  const pos = {
    x: Math.random() * 400 - 200,
    y: Math.random() * 400 - 200,
  };

  if (similarToNode) {
    const sn = graph.getNodePosition(similarToNode.id);
    pos.x = sn.x + Math.random() * 80 - 40;
    pos.y = sn.y + Math.random() * 80 - 40;
  }

  const node = graph.addNode(artist, pos);
  return node;
}

/**
 * Add similar artists to the graph
 * @param {Object} artist - Source artist
 * @param {Object} node - Source node
 * @param {Object} state - Application state
 * @param {Object} graph - Graph instance
 * @param {number} w - Window width
 * @param {number} h - Window height
 */
export function addSimilarArtists(artist, node, state, graph, w, h) {
  artist.similars
    .filter((s) => s.match >= MIN_MATCH)
    .forEach((s) => {
      let node2 = graph.getNode(s.to_id);

      if (!node2) {
        let a2 = state.findArtist(s.to_id);

        if (!a2) {
          a2 = {
            id: s.to_id,
            sim: true,
            name: s.to_name,
            label: s.to_name,
            playcount: 0,
            playRank: 0.001,
            similars: [],
            djplays: s.djplays || [],
            color: SIM_COLOR,
          };
          a2.color = getArtistColor(a2, state.djs);
          state.addArtist(a2);
        }

        node2 = addNodeToGraph(a2, graph, w, h, node);

        // Check for links to existing artists
        state.artists.forEach((a) => {
          if (!a.similars || a === artist || a === a2) {
            return;
          }

          const s4 = a.similars.find((_a) => _a.to_id === a2.id);
          if (s4) {
            const node3 = graph.getNode(a.id);
            if (node3) {
              graph.addEdge(node2, node3, s4);
            }
          }
        });
      }

      graph.addEdge(node, node2, s);
    });
}
